import settings from '../../../../settings';
import { get, webApi } from '../../../../utils/http';
import { sanitizeFileName } from '../../../../utils/normalizeName';

const TYPES = {
  empresa: 1,
  individual: 2,
  familia: 3,
};

const BASE_URL = settings.webApiCore;

/**
 * Helper function to download player report for a specific type
 * @param {string} playerId - The player ID
 * @param {number} type - Report type
 * @returns {Promise} API response with additional metadata
 */
const downloadPlayerReport = async (playerId, type) => {
  try {
    const response = await get(
      `${BASE_URL}/api/ines/player/${playerId}/report/${type}`,
      {
        extra: { responseType: 'blob' },
      },
    );
    return {
      playerId,
      type,
      response,
      success: true,
    };
  } catch (error) {
    return {
      playerId,
      type,
      error,
      success: false,
    };
  }
};

export const servicesReports = {
  /**
   * Get player report by type
   * When empresa type is requested, also downloads familia report in parallel
   * @param {string} playerId - The player ID
   * @param {number} type - Report type: 1 (empresa) | 2 (individual) | 3 (familia)
   * @returns {Promise} API response or array of responses for empresa type
   */
  getPlayerReport: async (playerId, type) => {
    const parsedType = TYPES[type] || TYPES.individual;

    // If empresa type is requested, download both empresa and familia reports in parallel
    if (parsedType === TYPES.empresa) {
      const [empresaResult, familiaResult] = await Promise.allSettled([
        downloadPlayerReport(playerId, TYPES.empresa),
        downloadPlayerReport(playerId, TYPES.familia),
      ]);

      return {
        empresa:
          empresaResult.status === 'fulfilled'
            ? empresaResult.value
            : { success: false, error: empresaResult.reason },
        familia:
          familiaResult.status === 'fulfilled'
            ? familiaResult.value
            : { success: false, error: familiaResult.reason },
      };
    }

    // For other types, use the original behavior
    return get(`${BASE_URL}/api/ines/player/${playerId}/report/${parsedType}`, {
      extra: { responseType: 'blob' },
    });
  },

  /**
   * Resend player report
   * @param {string} playerId - The player ID
   * @returns {Promise} API response
   */
  resendPlayerReport: async (playerId) => {
    return get(
      `${BASE_URL}/api/ines/player/${playerId}/report/${TYPES.individual}/resend`,
    );
  },

  /**
   * Get multiple player reports
   * When empresa type is requested, also downloads familia report in parallel
   * @param {Array<{playerId: string, type: string|number, playerName?: string, reportTypeLabel?: string}>} players - Array of player objects with playerId, type, and optional playerName and reportTypeLabel
   * @returns {Promise<Array>} Array of API responses with player info
   */
  getMultiplePlayerReports: async (players) => {
    const requests = players.flatMap(
      ({ playerId, type, playerName, reportTypeLabel }) => {
        const parsedType = TYPES[type] || TYPES.individual;

        // If empresa type is requested, create requests for both empresa and familia
        if (parsedType === TYPES.empresa) {
          return [
            // Empresa report request
            downloadPlayerReport(playerId, TYPES.empresa)
              .then((result) => ({
                ...result,
                playerName,
                reportTypeLabel: reportTypeLabel || 'Institución',
                originalType: type,
              }))
              .catch((error) => ({
                playerId,
                type: TYPES.empresa,
                playerName,
                reportTypeLabel: reportTypeLabel || 'Institución',
                originalType: type,
                error,
                success: false,
              })),
            // Familia report request
            downloadPlayerReport(playerId, TYPES.familia)
              .then((result) => ({
                ...result,
                playerName,
                reportTypeLabel: 'Familia',
                originalType: type,
              }))
              .catch((error) => ({
                playerId,
                type: TYPES.familia,
                playerName,
                reportTypeLabel: 'Familia',
                originalType: type,
                error,
                success: false,
              })),
          ];
        }

        // For other types, use the original behavior
        return [
          get(`${BASE_URL}/api/ines/player/${playerId}/report/${parsedType}`, {
            extra: { responseType: 'blob' },
          })
            .then((response) => ({
              playerId,
              type: parsedType,
              playerName,
              reportTypeLabel,
              originalType: type,
              response,
              success: true,
            }))
            .catch((error) => ({
              playerId,
              type: parsedType,
              playerName,
              reportTypeLabel,
              originalType: type,
              error,
              success: false,
            })),
        ];
      },
    );

    return Promise.allSettled(requests).then((results) =>
      results.map((result) => result.value),
    );
  },

  /**
   * Get multiple player reports in batches (for better performance with large datasets)
   * @param {Array<{playerId: string, type: string|number}>} players - Array of player objects
   * @param {number} batchSize - Number of requests per batch (default: 5)
   * @returns {Promise<Array>} Array of API responses with player info
   */
  getMultiplePlayerReportsBatched: async (players, batchSize = 5) => {
    const results = [];

    for (let i = 0; i < players.length; i += batchSize) {
      const batch = players.slice(i, i + batchSize);
      const batchResults = await servicesReports.getMultiplePlayerReports(
        batch,
      );
      results.push(...batchResults);
    }

    return results;
  },

  /**
   * Get player reports by player IDs with same type
   * @param {Array<string>} playerIds - Array of player IDs
   * @param {string|number} type - Report type for all players
   * @returns {Promise<Array>} Array of API responses
   */
  getPlayerReportsByIds: async (playerIds, type = 'individual') => {
    const players = playerIds.map((playerId) => ({ playerId, type }));
    return servicesReports.getMultiplePlayerReports(players);
  },

  /**
   * Download a single blob file
   * @param {Blob} blob - The blob data
   * @param {string} filename - The filename for the download
   */
  downloadBlob: (blob, filename) => {
    if (!blob || !(blob instanceof Blob)) {
      console.error('Invalid blob data');
      return;
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Download multiple reports from batched results
   * @param {Array} results - Results from getMultiplePlayerReportsBatched
   * @param {string} fileExtension - File extension (default: 'pdf')
   * @param {string} reportType - Type of report (e.g., 'Individual', 'Empresa')
   * @returns {Promise} Promise that resolves when all downloads are complete
   */
  downloadBatchedReports: (results, fileExtension = 'pdf', reportType = '') => {
    return new Promise((resolve, reject) => {
      if (!Array.isArray(results)) {
        console.error('Results must be an array');
        reject(new Error('Results must be an array'));
        return;
      }

      const successfulResults = results.filter(
        (result) => result.success && result.response,
      );

      if (successfulResults.length === 0) {
        console.warn('No successful reports to download');
        resolve({ downloaded: 0, total: 0 });
        return;
      }

      // Group results by player and original type to handle empresa+familia pairs
      const groupedResults = {};
      successfulResults.forEach((result) => {
        const key = `${result.playerId}_${result.originalType || result.type}`;
        if (!groupedResults[key]) {
          groupedResults[key] = [];
        }
        groupedResults[key].push(result);
      });

      const totalGroups = Object.keys(groupedResults).length;
      const totalDownloads = successfulResults.length;
      let downloadedSuccessfully = 0;

      // Create download promises for each group
      const groupPromises = Object.entries(groupedResults).map(
        ([groupKey, groupResults], groupIndex) => {
          return new Promise((groupResolve) => {
            // Create promises for all files in this group
            const filePromises = groupResults.map((result, fileIndex) => {
              return new Promise((fileResolve) => {
                try {
                  const { playerId, response, playerName, reportTypeLabel } =
                    result;

                  // Generate filename: "Full Name-ReportType.pdf"
                  let filename;
                  if (playerName && reportTypeLabel) {
                    // Clean the name to remove invalid characters for filenames
                    const reportTypeLabelParsed =
                      reportTypeLabel === 'Empresa'
                        ? 'Institución'
                        : reportTypeLabel;
                    const cleanName = sanitizeFileName(playerName);
                    filename = `${cleanName}-${reportTypeLabelParsed}.${fileExtension}`;
                  } else {
                    // Fallback to old format
                    filename = `player_report_${playerId}.${fileExtension}`;
                  }

                  // Calculate delay: group delay + file delay within group
                  const groupDelay = groupIndex * 200; // 200ms between groups
                  const fileDelay = fileIndex * 100; // 100ms between files in same group
                  const totalDelay = groupDelay + fileDelay;

                  setTimeout(() => {
                    try {
                      servicesReports.downloadBlob(response, filename);
                      downloadedSuccessfully++;
                      fileResolve({
                        success: true,
                        filename,
                        playerId,
                        reportTypeLabel,
                      });
                    } catch (downloadError) {
                      console.error(
                        `❌ Error downloading ${filename}:`,
                        downloadError,
                      );
                      fileResolve({
                        success: false,
                        error: downloadError,
                        filename,
                        playerId,
                        reportTypeLabel,
                      });
                    }
                  }, totalDelay);
                } catch (error) {
                  console.error(
                    `❌ Error preparing download for ${result.playerId}:`,
                    error,
                  );
                  fileResolve({
                    success: false,
                    error,
                    filename: `player_report_${result.playerId}.${fileExtension}`,
                  });
                }
              });
            });

            // Wait for all files in this group to complete
            Promise.allSettled(filePromises).then((fileResults) => {
              const groupSuccessful = fileResults.filter(
                (result) =>
                  result.status === 'fulfilled' && result.value.success,
              ).length;
              groupResolve({
                groupKey,
                successful: groupSuccessful,
                total: groupResults.length,
              });
            });
          });
        },
      );

      // Wait for all groups to complete
      Promise.allSettled(groupPromises).then((groupResults) => {
        const totalSuccessful = groupResults.reduce((sum, result) => {
          return (
            sum + (result.status === 'fulfilled' ? result.value.successful : 0)
          );
        }, 0);

        const failedDownloads = totalDownloads - totalSuccessful;
        resolve({
          downloaded: totalSuccessful,
          total: totalDownloads,
          failed: failedDownloads,
          groups: totalGroups,
        });
      });
    });
  },

  /**
   * Download all reports as a ZIP file (requires JSZip library)
   * @param {Array} results - Results from getMultiplePlayerReportsBatched
   * @param {string} zipFilename - Name for the ZIP file
   * @param {string} fileExtension - File extension for individual files (default: 'pdf')
   */
  downloadBatchedReportsAsZip: async (
    results,
    zipFilename = 'player_reports.zip',
    fileExtension = 'pdf',
  ) => {
    // Check if JSZip is available
    if (typeof window.JSZip === 'undefined') {
      console.error(
        'JSZip library is required for ZIP downloads. Include it in your project.',
      );
      return;
    }

    const zip = new window.JSZip();
    const successfulResults = results.filter(
      (result) => result.success && result.response,
    );

    if (successfulResults.length === 0) {
      console.warn('No successful reports to add to ZIP');
      return;
    }

    // Add each successful report to the ZIP
    successfulResults.forEach((result) => {
      const { playerId, response, playerName, reportTypeLabel } = result;

      // Generate filename: "Full Name-ReportType.pdf"
      let filename;
      if (playerName && reportTypeLabel) {
        // Clean the name to remove invalid characters for filenames
        const cleanName = sanitizeFileName(playerName);
        filename = `${cleanName}-${reportTypeLabel}.${fileExtension}`;
      } else {
        // Fallback to old format
        filename = `player_report_${playerId}.${fileExtension}`;
      }

      zip.file(filename, response);
    });

    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      servicesReports.downloadBlob(zipBlob, zipFilename);
    } catch (error) {
      console.error('Error creating ZIP file:', error);
    }
  },

  /**
   * Get and download batched reports in one call
   * @param {Array<{playerId: string, type: string|number, playerName?: string, reportTypeLabel?: string}>} players - Array of player objects
   * @param {number} batchSize - Number of requests per batch (default: 5)
   * @param {boolean} downloadAsZip - Whether to download as ZIP or individual files
   * @param {string} fileExtension - File extension (default: 'pdf')
   * @returns {Promise<Array>} Results array (maintains backward compatibility)
   */
  getBatchedReportsAndDownload: async (
    players,
    batchSize = 5,
    downloadAsZip = false,
    fileExtension = 'pdf',
  ) => {
    try {
      const results = await servicesReports.getMultiplePlayerReportsBatched(
        players,
        batchSize,
      );

      if (downloadAsZip) {
        await servicesReports.downloadBatchedReportsAsZip(
          results,
          `player_reports_${new Date().toISOString().split('T')[0]}.zip`,
          fileExtension,
        );
        const successfulResults = results.filter(
          (result) => result.success && result.response,
        );
      } else {
        // Wait for all individual downloads to complete
        const downloadInfo = await servicesReports.downloadBatchedReports(
          results,
          fileExtension,
        );
      }
      return results;
    } catch (error) {
      console.error('Error in batch download process:', error);
      throw error;
    }
  },
};
